import isEqual from 'lodash/isEqual';
import { RESULT_INSUFFICIENT_REASON_LABEL } from 'src/features/proofs/constants';
import { getAttrEntryResult } from 'src/helpers/getEntryStatusIcon';
import { EntryResultEnum, ProofResultInsufficientReasonEnum } from 'src/ts/enums';
import { IProof, ProofResultInsufficientReasonType, ProofType } from 'src/ts/interfaces';

// OFFER_LETTER is not yet in the SDK's ProofTypeEnum (pending SDK regeneration after AIC-778).
// Once the SDK is updated, replace this with ProofTypeEnum.OfferLetter.
export const OFFER_LETTER_TYPE = 'OFFER_LETTER' as ProofType;

// CPA_LETTER is not yet in the SDK's ProofTypeEnum (pending SDK regeneration after AIC-778).
// Once the SDK is updated, replace this with ProofTypeEnum.CpaLetter.
export const CPA_LETTER_TYPE = 'CPA_LETTER' as ProofType;

export const PENDING_EMPLOYER_VERIFICATION = 'PENDING_EMPLOYER_VERIFICATION';

export const UNVERIFIED_OFFER_LETTER_REASON =
  ProofResultInsufficientReasonEnum.UNVERIFIED_OFFER_LETTER;

// Identifies the auto-INSUFFICIENT offer letter case from ITL-2687.
// Composite check (type + result + reason code) per product guidance: the
// `is_automatic_review` flag is intentionally NOT used for this case because
// setting it would impact other auto-rejection metrics and dashboards.
// Use the negation (`!isUnverifiedOfferLetter(proof)`) at the suppression
// sites (Edit button gating, dropdown filter, carousel filter).
export const isUnverifiedOfferLetter = (proof: IProof): boolean =>
  proof.type === OFFER_LETTER_TYPE &&
  proof.result === 'UNDETERMINED' &&
  !!proof.result_insufficient_reasons?.includes(UNVERIFIED_OFFER_LETTER_REASON);

// Greystar / OLV-enabled property path: re-queue + AI pipeline.
export const RECLASSIFY_TO_OL_CONFIRM_TEXT =
  'Changing to Offer Letter will return this review to the queue. The AI extraction pipeline will run and the review will be assigned to an OLV-qualified reviewer. Any work done on this submission will be lost.';

// ITL-2772: non-OLV property path. The proof is auto-marked Insufficient
// (Unverified Offer Letter) and the analyst stays on the entry. No re-queue,
// no AI pipeline, no reviewer reassignment — the Greystar copy above would
// be actively misleading on this branch. Copy is explicit about the
// irreversibility (no Edit button on the resulting AV Ruled row, no
// second reclassification path back) so the analyst can't accidentally
// commit an unrecoverable state.
export const RECLASSIFY_TO_OL_GATED_CONFIRM_TEXT =
  "Reclassifying to Offer Letter will permanently rule this document as Insufficient – Unverified. You won't be able to reverse this or reclassify the document again. Continue?";

export const RECLASSIFY_FROM_OL_CONFIRM_TEXT =
  'Changing from Offer Letter will update review routing and clear OLV-specific data.';

export const RECLASSIFY_CHECK_SET_CONFIRM_TEXT =
  'Changing this document type changes which list checks run. The checks will re-run, this review will return to the queue, and you will be unassigned. Any work done on this submission will be lost.';

export const involvesOfferLetter = (oldType: string, newType: string): boolean =>
  oldType !== newType && (oldType === OFFER_LETTER_TYPE || newType === OFFER_LETTER_TYPE);

// ITL-2772: discriminator emitted inside the proof response data by
// proof_view.ex's maybe_put_reclassification_status. The BE returns one
// of these two strings; the FE narrows via this guard so unknown / undefined
// values fall through to the no-routing branch instead of being silently
// treated as "requeued" (the old code's behavior on undefined).
export type ReclassificationStatus = 'requeued' | 'auto_insufficient';

export const isReclassificationStatus = (s: unknown): s is ReclassificationStatus =>
  s === 'requeued' || s === 'auto_insufficient';

// ITL-2772: derives the property's OLV feature state from the loaded
// IProperty. The BE serializer emits `property_features` from
// property_view.ex; missing/loading data is treated as "disabled" so the
// gated copy (no work-lost language) is shown — the BE's authoritative
// gate runs after the click regardless of what the FE shows.
export const isPropertyOlvEnabled = (
  property?: { property_features?: Array<{ code?: string; state?: string }> } | null
): boolean =>
  !!property?.property_features?.some(
    (f) => f.code === 'offer_letter_verification' && f.state === 'enabled'
  );

export interface FormValues {
  result: string;
  reason: string | string[];
  documentType: string;
  olvReasons?: string[];
}

export enum DocumentIconType {
  CLEAN = 'cardSelectDoc_clean',
  EDITED = 'cardSelectDoc_edited',
  INSUFFICIENT = 'cardSelectDoc_insufficient',
  INSUFFICIENT_DOCUMENTATION = 'cardSelectDoc_insufficient_documentation'
}

export const getProofReason = (proof: IProof): string | string[] => {
  if (proof.result === 'EDITED' && proof.result_edited_reason?.length > 0) {
    return proof.result_edited_reason.length === 1
      ? proof.result_edited_reason[0]
      : proof.result_edited_reason;
  }
  if (proof.result === 'UNDETERMINED' && proof.result_insufficient_reason) {
    return proof.result_insufficient_reason;
  }
  return '';
};

/**
 * ITL-2769: Returns the effective ruling for a proof, accounting for the
 * localFormData precedence rule. `localFormData[id].result === 'PENDING'` is
 * the form's initial state (set by DocumentViewer's init effect when the
 * analyst merely visits a proof) — NOT a real saved ruling. Only let
 * savedData override the server's proof.result when it's a terminal value.
 *
 * Use this everywhere a component needs to know "what is this proof's
 * current ruling," including pre-server-write states.
 */
export const getEffectiveProofResult = (
  proof: IProof,
  savedData: FormValues | undefined
): string => {
  const savedHasTerminalResult = !!savedData?.result && savedData.result !== 'PENDING';
  return savedHasTerminalResult ? (savedData?.result as string) : proof.result || 'PENDING';
};

export const getProofData = (proof: IProof | undefined, savedData?: FormValues) => {
  if (!proof) {
    return { result: 'PENDING', reason: '', documentType: '', olvReasons: [] as string[] };
  }
  let result: string;
  let reason: string | string[];
  let documentType: string;
  let olvReasons: string[];

  if (proof.is_automatic_review && !savedData) {
    result = proof.result || 'PENDING';
    documentType = proof.type || '';
    reason = getProofReason(proof);
    olvReasons = proof.result_insufficient_reasons ?? [];
  } else {
    // ITL-2769: see getEffectiveProofResult above for the precedence rule.
    result = getEffectiveProofResult(proof, savedData);
    reason = savedData?.reason || getProofReason(proof);
    documentType = savedData?.documentType || proof.type || '';
    olvReasons = savedData?.olvReasons ?? proof.result_insufficient_reasons ?? [];
  }

  return { result, reason, documentType, olvReasons };
};

export const hasAllRequiredFields = (
  result: string,
  documentType: string,
  reason: string | string[],
  olvReasons?: string[]
): boolean => {
  if (!result || result === 'PENDING') return false;
  if (!documentType) return false;
  if (result === EntryResultEnum.Edited) {
    if (Array.isArray(reason)) {
      return reason.length > 0 && reason.every((r) => r.trim() !== '');
    }
    return reason !== undefined && reason !== null && reason.trim() !== '';
  }
  if (result === EntryResultEnum.Undetermined) {
    const hasOlvReasons = olvReasons && olvReasons.length > 0 && documentType === OFFER_LETTER_TYPE;
    if (hasOlvReasons) return true;
    if (Array.isArray(reason)) {
      return reason.length > 0 && reason.every((r) => r.trim() !== '');
    }
    return reason !== undefined && reason !== null && reason.trim() !== '';
  }
  return true;
};

const asSortedList = (value: string | string[] | undefined): string[] =>
  (Array.isArray(value) ? value : value ? [value] : []).filter(Boolean).sort();

export const isAutomaticRulingOverridden = (proof: IProof, savedData?: FormValues): boolean => {
  if (!proof.is_automatic_review || !savedData) return false;
  const automatic = getProofData(proof);
  const resultNeedsReason = savedData.result === 'EDITED' || savedData.result === 'UNDETERMINED';
  return (
    savedData.result !== automatic.result ||
    savedData.documentType !== automatic.documentType ||
    (resultNeedsReason &&
      !isEqual(asSortedList(savedData.reason), asSortedList(automatic.reason))) ||
    (savedData.documentType === OFFER_LETTER_TYPE &&
      !isEqual(asSortedList(savedData.olvReasons), asSortedList(automatic.olvReasons)))
  );
};

export const needsAnalystRuling = (proof: IProof, savedData?: FormValues): boolean =>
  (!proof.is_automatic_review || isAutomaticRulingOverridden(proof, savedData)) &&
  !isUnverifiedOfferLetter(proof);

// ITL-2816: pure submit-gate predicate. Returns true iff every manually-
// reviewable proof of the SUBMITTED entry has all required ruling fields.
// Mirrors the multi-entry fix: the gate must evaluate the entry being
// submitted (selected), not the assigned entry. Unverified offer letters
// and untouched AV proofs are skipped, like the server-side reconciliation
// guard; AV proofs the analyst overrode are also gated here.
export const isEntryRuled = (
  proofs: IProof[],
  localFormData: Record<string, FormValues>
): boolean => {
  const manuallyReviewableProofs = proofs.filter((proof) =>
    needsAnalystRuling(proof, localFormData[proof.id])
  );

  if (manuallyReviewableProofs.length === 0) return true;

  return manuallyReviewableProofs.every((proof) => {
    const savedData = localFormData[proof.id];
    const { result, reason, documentType, olvReasons } = getProofData(proof, savedData);
    return hasAllRequiredFields(result, documentType, reason, olvReasons);
  });
};

export const getIconPath = (iconType: string): string => {
  switch (iconType) {
    case DocumentIconType.CLEAN:
      return '/assets/images/clean.png';
    case DocumentIconType.EDITED:
      return '/assets/images/edited.png';
    case DocumentIconType.INSUFFICIENT:
    case DocumentIconType.INSUFFICIENT_DOCUMENTATION:
      return '/assets/images/insufficient.png';
    default:
      return '/assets/images/pending.svg';
  }
};

export const getProofRuling = (
  proof: IProof,
  localFormData: Record<string, FormValues>
): { result: string; icon: string } | null => {
  const savedData = localFormData[proof.id];
  const { result, reason, documentType, olvReasons } = getProofData(proof, savedData);

  if (!hasAllRequiredFields(result, documentType, reason, olvReasons)) return null;

  const { icon } = getAttrEntryResult(result as any);
  return { result, icon: getIconPath(icon) };
};

export const getRulingIcon = (proof: IProof, localFormData: Record<string, FormValues>): string =>
  getProofRuling(proof, localFormData)?.icon ?? '/assets/images/pending.svg';

/**
 * Build auto-rule updates for offer letter proofs when blocked_edited.
 * Sets each unruled offer letter to UNDETERMINED.
 * Returns prev unchanged (same reference) if no updates needed.
 *
 */
export function buildAutoRuleUpdates(
  offerLetterProofs: IProof[],
  prev: Record<string, FormValues>
): Record<string, FormValues> {
  const updates: Record<string, FormValues> = {};
  let hasChanges = false;

  const reasonCode = UNVERIFIED_OFFER_LETTER_REASON;

  for (const proof of offerLetterProofs) {
    const existing = prev[proof.id];
    if (!existing?.result || existing.result === 'PENDING') {
      hasChanges = true;
      updates[proof.id] = {
        result: 'UNDETERMINED',
        reason: reasonCode,
        documentType: proof.type || '',
        olvReasons: [reasonCode]
      };
    }
  }

  return hasChanges ? { ...prev, ...updates } : prev;
}

/**
 * Builds the Send-Report batch PATCH payload for a proof.
 *
 * ITL-2968: for the effective OFFER_LETTER type the `note` key is omitted —
 * the backend writes the official per-reason report copy on terminal
 * rulings. Non-OL proofs keep the legacy label-derived note. Effective type
 * mirrors the backend guard (ReportCopy.put_report_note/2).
 */
export function buildProofUpdateData(
  proof: IProof,
  savedData: FormValues
): { id: string } & Partial<IProof> {
  const isOfferLetter = (savedData.documentType || proof.type) === OFFER_LETTER_TYPE;

  // `olvReasons` is OL-only form state, but the DocumentViewer init effect
  // re-seeds it from the DB without a type gate (an OL reclassified away
  // still carries its old reasons after a refresh) — enforce the invariant
  // here so a non-OL payload never carries olvReasons or the CUSTOM marker.
  const olvReasons = isOfferLetter ? savedData.olvReasons : undefined;

  let note = '';
  if (!isOfferLetter && savedData.result === 'UNDETERMINED' && savedData.reason) {
    const insufficientReason = Array.isArray(savedData.reason)
      ? savedData.reason[0]
      : savedData.reason;

    note =
      RESULT_INSUFFICIENT_REASON_LABEL[
        insufficientReason as keyof typeof RESULT_INSUFFICIENT_REASON_LABEL
      ] ||
      insufficientReason ||
      '';
  }

  const insufficientReasons = olvReasons?.length
    ? (olvReasons as IProof['result_insufficient_reasons'])
    : savedData.result === 'UNDETERMINED' && savedData.reason && savedData.reason !== 'NONE'
    ? ([savedData.reason] as IProof['result_insufficient_reasons'])
    : null;

  return {
    id: proof.id,
    result: savedData.result as IProof['result'],
    result_edited_reason:
      savedData.result === 'EDITED'
        ? ((Array.isArray(savedData.reason)
            ? savedData.reason
            : savedData.reason
            ? [savedData.reason]
            : []) as IProof['result_edited_reason'])
        : proof.result_edited_reason,
    result_insufficient_reason:
      savedData.result === 'UNDETERMINED'
        ? olvReasons && olvReasons.length > 0
          ? ('CUSTOM' as ProofResultInsufficientReasonType)
          : ((Array.isArray(savedData.reason)
              ? savedData.reason[0] || ''
              : savedData.reason || '') as ProofResultInsufficientReasonType)
        : proof.result_insufficient_reason,
    type: savedData.documentType as IProof['type'],
    ...(isOfferLetter ? {} : { note }),
    result_insufficient_reasons: insufficientReasons
  };
}
